/**
 * SchedulingPanel.tsx — App Integrity "Scheduling" tab (CJ 2026-07-30).
 *
 * Configures the automatic suite runs: cadence (weekly at first, monthly
 * once trust builds), the run time (UTC), and who gets emailed — ONLY when
 * a run fails ("review by exception"), plus the watchdog that emails when a
 * due run never happened at all.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import {
  fetchScheduleSettings,
  saveScheduleSettings,
  type IntegrityScheduleSettings,
} from '../../../services/appIntegrityService';
import { statusChipColor } from './integrityDisplay';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

interface SchedulingPanelProps {
  notify: (message: string, severity: 'success' | 'error' | 'info') => void;
}

export const SchedulingPanel = ({ notify }: SchedulingPanelProps) => {
  const [settings, setSettings] = useState<IntegrityScheduleSettings | null>(null);
  const [recipientsText, setRecipientsText] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void fetchScheduleSettings().then((s) => {
      setSettings(s);
      setRecipientsText(s.recipients.join(', '));
    });
  }, []);

  if (!settings) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const patch = (p: Partial<IntegrityScheduleSettings>) => {
    setSettings((prev) => (prev ? { ...prev, ...p } : prev));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const fresh = await saveScheduleSettings({
        enabled: settings.enabled,
        cadence: settings.cadence,
        weekday: settings.weekday,
        hourUtc: settings.hourUtc,
        recipients: recipientsText.split(',').map((r) => r.trim()).filter(Boolean),
      });
      setSettings(fresh);
      setRecipientsText(fresh.recipients.join(', '));
      setDirty(false);
      notify('Schedule saved', 'success');
    } catch {
      notify('Failed to save schedule', 'error');
    } finally {
      setSaving(false);
    }
  };

  const localHint = (() => {
    const d = new Date();
    d.setUTCHours(settings.hourUtc, 0, 0, 0);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  })();

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, maxWidth: 720 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
        Scheduled integrity runs
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The full suite runs automatically on this schedule. Email goes out <b>only when a run
        fails</b> — a green run stays quiet (the history keeps every result). If a due run never
        happens at all (service down, run stuck), a watchdog email goes out instead, so silence
        always means &ldquo;ran and passed&rdquo;.
      </Typography>

      {!settings.internalJobConfigured && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          <code>INTERNAL_JOB_SECRET</code> is not configured on the server (needs ≥ 32 chars) —
          scheduled runs cannot authenticate and will error until it is set.
        </Alert>
      )}
      {!settings.emailConfigured && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          <code>SENDGRID_API_KEY</code> is not configured — failure and watchdog emails will be
          logged but not sent.
        </Alert>
      )}

      <FormControlLabel
        control={
          <Switch checked={settings.enabled} onChange={(e) => patch({ enabled: e.target.checked })} />
        }
        label={settings.enabled ? 'Scheduled runs are ON' : 'Scheduled runs are OFF'}
        sx={{ mb: 1.5, display: 'block' }}
      />

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Cadence</InputLabel>
          <Select
            value={settings.cadence}
            label="Cadence"
            onChange={(e) => patch({ cadence: e.target.value as 'weekly' | 'monthly' })}
          >
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly (1st)</MenuItem>
          </Select>
        </FormControl>
        {settings.cadence === 'weekly' && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Day</InputLabel>
            <Select
              value={settings.weekday}
              label="Day"
              onChange={(e) => patch({ weekday: Number(e.target.value) })}
            >
              {WEEKDAYS.map((d, i) => (
                <MenuItem key={d} value={i}>{d}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Hour (UTC)</InputLabel>
          <Select
            value={settings.hourUtc}
            label="Hour (UTC)"
            onChange={(e) => patch({ hourUtc: Number(e.target.value) })}
          >
            {Array.from({ length: 24 }, (_, h) => (
              <MenuItem key={h} value={h}>{String(h).padStart(2, '0')}:00 UTC</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
          ≈ {localHint} your local time
        </Typography>
      </Box>

      <TextField
        fullWidth
        size="small"
        label="Failure-alert recipients (comma-separated emails)"
        placeholder="cj@bgcengineering.ca, glai@bgcengineering.ca"
        value={recipientsText}
        onChange={(e) => {
          setRecipientsText(e.target.value);
          setDirty(true);
        }}
        helperText="Emailed only on a failed/errored run — and by the missed-run watchdog."
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          size="small"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save />}
          disabled={!dirty || saving}
          onClick={handleSave}
        >
          Save schedule
        </Button>
        {settings.enabled && settings.nextDueAt && (
          <Typography variant="body2" color="text.secondary">
            Next run due: <b>{new Date(settings.nextDueAt).toLocaleString()}</b>
          </Typography>
        )}
      </Box>

      {settings.lastScheduledRun && (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Last scheduled run:
          </Typography>
          <Chip
            size="small"
            color={statusChipColor(settings.lastScheduledRun.status)}
            label={settings.lastScheduledRun.status.toUpperCase()}
          />
          <Typography variant="body2" color="text.secondary">
            {new Date(settings.lastScheduledRun.startedAt).toLocaleString()} ·{' '}
            {settings.lastScheduledRun.passCount}/{settings.lastScheduledRun.fixtureCount} passed
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
